import React, { useEffect, useState } from 'react';
import { getPlans, getMySubscription, createPayment } from '../api/client';
import { Crown, Zap, Shield, Check, Loader2 } from 'lucide-react';

interface Plan {
  id: number;
  name: string;
  price: number;
  duration_days: number;
  max_searches: number;
  check_interval_seconds: number;
  seller_filter: boolean;
  advanced_filters: boolean;
  group_enabled: boolean;
}

export const SubscriptionPage: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [mySub, setMySub] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansData, subData] = await Promise.all([
        getPlans(),
        getMySubscription().catch(() => null),
      ]);
      setPlans(plansData);
      setMySub(subData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleBuy = async (planId: number) => {
    setBuying(planId);
    try {
      const payment = await createPayment(planId);
      const tg = (window as any).Telegram?.WebApp;

      if (payment.payment_url) {
        if (tg?.openLink) {
          tg.openLink(payment.payment_url);
        } else {
          window.open(payment.payment_url, '_blank');
        }
      } else {
        alert('\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u0441\u043e\u0437\u0434\u0430\u0442\u044c \u0441\u0441\u044b\u043b\u043a\u0443 \u043d\u0430 \u043e\u043f\u043b\u0430\u0442\u0443. \u041f\u043e\u043f\u0440\u043e\u0431\u0443\u0439\u0442\u0435 \u043f\u043e\u0437\u0436\u0435.');
      }
    } catch (e: any) {
      alert('\u041e\u0448\u0438\u0431\u043a\u0430: ' + (e.response?.data?.detail || '\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u0441\u043e\u0437\u0434\u0430\u0442\u044c \u043f\u043b\u0430\u0442\u0435\u0436'));
    } finally {
      setBuying(null);
    }
  };

  const formatPrice = (price: number) => {
    return (price / 100).toLocaleString('ru-RU');
  };

  const getPlanIcon = (name: string) => {
    switch (name) {
      case 'START': return <Zap size={20} />;
      case 'PRO': return <Shield size={20} />;
      case 'EXPERT': return <Crown size={20} />;
      default: return <Zap size={20} />;
    }
  };

  const getPlanSubtitle = (name: string) => {
    switch (name) {
      case 'START': return '\u0414\u043b\u044f \u0437\u043d\u0430\u043a\u043e\u043c\u0441\u0442\u0432\u0430 \u0441 \u0441\u0435\u0440\u0432\u0438\u0441\u043e\u043c';
      case 'PRO': return '\u0414\u043b\u044f \u0442\u0435\u0445, \u043a\u0442\u043e \u0437\u0430\u0440\u0430\u0431\u0430\u0442\u044b\u0432\u0430\u0435\u0442 \u043d\u0430 \u043f\u0435\u0440\u0435\u043f\u0440\u043e\u0434\u0430\u0436\u0435';
      case 'EXPERT': return '\u0414\u043b\u044f \u043f\u0440\u043e\u0444\u0435\u0441\u0441\u0438\u043e\u043d\u0430\u043b\u044c\u043d\u043e\u0433\u043e \u043c\u043e\u043d\u0438\u0442\u043e\u0440\u0438\u043d\u0433\u0430';
      default: return '';
    }
  };

  const getPlanCta = (name: string) => {
    switch (name) {
      case 'START': return '\u041f\u043e\u043f\u0440\u043e\u0431\u043e\u0432\u0430\u0442\u044c';
      case 'PRO': return '\u041d\u0430\u0447\u0430\u0442\u044c \u043e\u0445\u043e\u0442\u0443';
      case 'EXPERT': return '\u0420\u0430\u0431\u043e\u0442\u0430\u0442\u044c \u043d\u0430 \u043c\u0430\u043a\u0441\u0438\u043c\u0443\u043c\u0435';
      default: return '\u041a\u0443\u043f\u0438\u0442\u044c';
    }
  };

  const getPlanInterval = (name: string) => {
    switch (name) {
      case 'START': return '⚡';
      case 'PRO': return '⚡⚡';
      case 'EXPERT': return '⚡⚡⚡';
      default: return '—';
    }
  };

  const getPlanColor = (name: string) => {
    switch (name) {
      case 'START': return '#10b981';
      case 'PRO': return '#3b82f6';
      case 'EXPERT': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>💎 Подписка</h1>

      {mySub && (
        <div style={styles.currentCard}>
          <div style={styles.currentHeader}>
            <span style={styles.currentLabel}>Текущая подписка</span>
            <span style={styles.currentBadge}>АКТИВНА</span>
          </div>
          <div style={styles.currentPlan}>{mySub.plan?.name || 'Пробный период'}</div>
          <div style={styles.currentDate}>
            До: {new Date(mySub.expires_at).toLocaleDateString('ru-RU')}
          </div>
        </div>
      )}

      <div style={styles.plansList}>
        {plans.filter(p => p.name !== 'FREE_TRIAL').map((plan) => (
          <div
            key={plan.id}
            style={{
              ...styles.planCard,
              borderColor: getPlanColor(plan.name),
              borderWidth: mySub?.plan_id === plan.id ? '2px' : '1px',
            }}
          >
            {plan.name === 'PRO' && (
              <div style={styles.popularBadge}>ПОПУЛЯРНЫЙ</div>
            )}

            <div style={styles.planHeader}>
              <div style={{ ...styles.planIcon, backgroundColor: getPlanColor(plan.name) + '20', color: getPlanColor(plan.name) }}>
                {getPlanIcon(plan.name)}
              </div>
              <div style={styles.planInfo}>
                <div style={styles.planName}>{plan.name}</div>
                <div style={styles.planPrice}>
                  {formatPrice(plan.price)} ₽<span style={styles.planPeriod}>/мес</span>
                </div>
              </div>
            </div>

            <div style={styles.planSubtitle}>{getPlanSubtitle(plan.name)}</div>

            <div style={styles.features}>
              <div style={styles.feature}>
                <Check size={14} style={{ color: '#10b981' }} />
                <span>{plan.max_searches} {plan.max_searches % 10 === 1 && plan.max_searches % 100 !== 11 ? 'поисковая ссылка' : ([2, 3, 4].includes(plan.max_searches % 10) && ![12, 13, 14].includes(plan.max_searches % 100) ? 'поисковые ссылки' : 'поисковых ссылок')}</span>
              </div>
              <div style={styles.feature}>
                <Check size={14} style={{ color: '#10b981' }} />
                <span>Скорость: {getPlanInterval(plan.name)}</span>
              </div>
              {plan.name === 'START' && (
                <>
                  <div style={styles.feature}>
                    <Check size={14} style={{ color: '#10b981' }} />
                    <span>Уведомления в Telegram</span>
                  </div>
                  <div style={styles.feature}>
                    <Check size={14} style={{ color: '#10b981' }} />
                    <span>Доступ к мини-приложению</span>
                  </div>
                </>
              )}
              {plan.name === 'PRO' && (
                <>
                  <div style={styles.feature}>
                    <Check size={14} style={{ color: '#10b981' }} />
                    <span>Всё из тарифа Start</span>
                  </div>
                  <div style={styles.feature}>
                    <Check size={14} style={{ color: '#10b981' }} />
                    <span>Фильтр продавцов</span>
                  </div>
                  <div style={styles.feature}>
                    <Check size={14} style={{ color: '#10b981' }} />
                    <span>Скрыть продавца</span>
                  </div>
                </>
              )}
              {plan.name === 'EXPERT' && (
                <>
                  <div style={styles.feature}>
                    <Check size={14} style={{ color: '#10b981' }} />
                    <span>Всё из PRO</span>
                  </div>
                  {plan.group_enabled && (
                    <div style={styles.feature}>
                      <Check size={14} style={{ color: '#10b981' }} />
                      <span>Групповые уведомления</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <button
              style={{
                ...styles.buyBtn,
                backgroundColor: getPlanColor(plan.name),
                opacity: mySub?.plan_id === plan.id || buying === plan.id ? 0.5 : 1,
              }}
              disabled={mySub?.plan_id === plan.id || buying === plan.id}
              onClick={() => handleBuy(plan.id)}
            >
              {buying === plan.id ? (
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
              ) : mySub?.plan_id === plan.id ? (
                'Активно'
              ) : (
                getPlanCta(plan.name)
              )}
            </button>
            {plan.name === 'PRO' && (
              <div style={styles.popularNote}>Большинство пользователей выбирают PRO</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '16px',
    maxWidth: '480px',
    margin: '0 auto',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e5e7eb',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    margin: '0 0 16px 0',
    color: 'var(--tg-theme-text-color, #000)',
  },
  currentCard: {
    backgroundColor: 'var(--tg-theme-secondary-bg-color, #fff)',
    borderRadius: '16px',
    padding: '16px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  currentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  currentLabel: {
    fontSize: '12px',
    color: 'var(--tg-theme-hint-color, #666)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  currentBadge: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#10b981',
    backgroundColor: '#10b98120',
    padding: '4px 10px',
    borderRadius: '20px',
  },
  currentPlan: {
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--tg-theme-text-color, #000)',
  },
  currentDate: {
    fontSize: '14px',
    color: 'var(--tg-theme-hint-color, #666)',
    marginTop: '4px',
  },
  plansList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  popularBadge: {
    position: 'absolute',
    top: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    padding: '4px 12px',
    borderRadius: '20px',
  },
  popularNote: {
    marginTop: '10px',
    fontSize: '12px',
    textAlign: 'center',
    color: 'var(--tg-theme-hint-color, #666)',
  },
  planSubtitle: {
    fontSize: '13px',
    color: 'var(--tg-theme-hint-color, #666)',
    marginBottom: '12px',
    marginTop: '-6px',
  },
  planCard: {
    position: 'relative',
    backgroundColor: 'var(--tg-theme-secondary-bg-color, #fff)',
    borderRadius: '16px',
    padding: '16px',
    border: '1px solid var(--tg-theme-hint-color, #e0e0e0)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  planHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '14px',
  },
  planIcon: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  planInfo: {
    flex: 1,
  },
  planName: {
    fontSize: '16px',
    fontWeight: 700,
    color: 'var(--tg-theme-text-color, #000)',
  },
  planPrice: {
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--tg-theme-text-color, #000)',
  },
  planPeriod: {
    fontSize: '14px',
    fontWeight: 400,
    color: 'var(--tg-theme-hint-color, #666)',
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '14px',
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: 'var(--tg-theme-text-color, #000)',
  },
  buyBtn: {
    width: '100%',
    padding: '12px',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
};
